import { useState } from 'react';
import loadable from '@loadable/component';

import { usePortfolios } from '@/hooks/portfolio/usePortfolio';
import { useAggregatePortfolio } from '@/hooks/portfolio/useAggregatePortfolio';
import useUser from '@/hooks/useUser';

import type { Portfolios } from '@/hooks/portfolio/usePortfolio';
import type { AggregatePortfolio } from '@/hooks/portfolio/useAggregatePortfolio';
import type { ProfileState } from '@/hooks/useUser';

import { PortfoliosLoadingState } from './components/PortfoliosLoadingState';
import { PortfolioEmptyState } from './components/PortfolioEmptyState';

import { getDefaultPortfolioId } from './utils/getDefaultPortfolioId';

const Portfolios = loadable(() => import('./Portfolios'), {
  resolveComponent: (c) => c.Portfolios,
  fallback: <PortfoliosLoadingState />,
});

const AggregatePortfolio = loadable(() => import('./AggregatePortfolio'), {
  resolveComponent: (c) => c.AggregatePortfolio,
  fallback: <PortfoliosLoadingState />,
});

interface Props {
  portfolios: Portfolios;
  aggregatePortfolio: AggregatePortfolio;
  user: ProfileState;
}

const PopulatedPortfoliosSection = ({
  portfolios,
  aggregatePortfolio,
  user,
}: Props) => {
  const [selectedPortfolioId, setSelectedPortfolioId] = useState(() =>
    getDefaultPortfolioId(
      user.portfolio?.dashboardDefaultPortfolioId ?? null,
      portfolios
    )
  );

  if (selectedPortfolioId === null) {
    return (
      <AggregatePortfolio
        aggregatePortfolio={aggregatePortfolio}
        selectedPortfolioId={selectedPortfolioId}
        setSelectedPortfolioId={setSelectedPortfolioId}
      />
    );
  }

  return (
    <Portfolios
      selectedPortfolioId={selectedPortfolioId}
      setSelectedPortfolioId={setSelectedPortfolioId}
    />
  );
};

export const PortfoliosSection = () => {
  const { data: user } = useUser();
  const { data: portfolios, status: portfoliosStatus } = usePortfolios();

  const hasPortfolios = portfolios && portfolios.length > 0;

  const { data: aggregatePortfolio, status: aggregatePortfolioStatus } =
    useAggregatePortfolio({ enabled: hasPortfolios });

  if (
    portfoliosStatus === 'pending' ||
    (hasPortfolios && aggregatePortfolioStatus === 'pending')
  ) {
    return <PortfoliosLoadingState />;
  }

  if (!hasPortfolios || !aggregatePortfolio) {
    return <PortfolioEmptyState />;
  }

  return (
    <PopulatedPortfoliosSection
      portfolios={portfolios}
      aggregatePortfolio={aggregatePortfolio}
      user={user}
    />
  );
};
